import React, { useState } from "react";
import { Box, Typography, Paper, Chip, Modal, Backdrop, IconButton, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Reply as ReplyIcon, DoneAll, Close, ZoomIn, InsertDriveFile, Download, Visibility } from "@mui/icons-material";

// Parse markdown-style bold (**text**) and emojis
const parseMessageText = (text, highlight) => {
  const emojiRegex =
    /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

  // First, handle bold markdown (**text**)
  const boldRegex = /\*\*(.+?)\*\*/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before bold
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    // Add bold text (without **)
    parts.push({ type: "bold", content: match[1] });
    lastIndex = boldRegex.lastIndex;
  }
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }

  // If no bold found, treat entire text as regular
  if (parts.length === 0) {
    parts.push({ type: "text", content: text });
  }

  // Process each part for emojis and highlight
  return parts.map((part, partIndex) => {
    if (part.type === "bold") {
      const emojiParts = part.content.split(emojiRegex).map((emojiPart, eIndex) => {
        if (emojiRegex.test(emojiPart)) {
          const hexCode = emojiPart.codePointAt(0).toString(16);
          return (
            <Box
              key={`${partIndex}-${eIndex}`}
              component="img"
              src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/${hexCode}.png`}
              alt={emojiPart}
              sx={{
                width: "1.6em",
                height: "1.6em",
                display: "inline-block",
                verticalAlign: "bottom",
                mx: "2px",
              }}
            />
          );
        }
        return emojiPart;
      });
      return (
        <Box
          key={partIndex}
          component="span"
          sx={{ fontWeight: 700 }}
        >
          {emojiParts}
        </Box>
      );
    } else {
      // Regular text with emoji and highlight handling
      if (!highlight) {
        return part.content.split(emojiRegex).map((emojiPart, eIndex) => {
          if (emojiRegex.test(emojiPart)) {
            const hexCode = emojiPart.codePointAt(0).toString(16);
            return (
              <Box
                key={`${partIndex}-${eIndex}`}
                component="img"
                src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/${hexCode}.png`}
                alt={emojiPart}
                sx={{
                  width: "1.6em",
                  height: "1.6em",
                  display: "inline-block",
                  verticalAlign: "bottom",
                  mx: "2px",
                }}
              />
            );
          }
          return emojiPart;
        });
      }

      // With highlight
      const highlightParts = part.content.split(new RegExp(`(${highlight})`, "gi"));
      return highlightParts.map((hPart, hIndex) => {
        if (hPart.toLowerCase() === highlight.toLowerCase()) {
          return (
            <Box
              key={`${partIndex}-${hIndex}`}
              component="span"
              sx={{ bgcolor: "#fef08a", color: "#000", borderRadius: "2px", px: 0.5 }}
            >
              {hPart}
            </Box>
          );
        }
        return hPart.split(emojiRegex).map((emojiPart, eIndex) => {
          if (emojiRegex.test(emojiPart)) {
            const hexCode = emojiPart.codePointAt(0).toString(16);
            return (
              <Box
                key={`${partIndex}-${hIndex}-${eIndex}`}
                component="img"
                src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/${hexCode}.png`}
                alt={emojiPart}
                sx={{
                  width: "1.6em",
                  height: "1.6em",
                  display: "inline-block",
                  verticalAlign: "bottom",
                  mx: "2px",
                }}
              />
            );
          }
          return emojiPart;
        });
      });
    }
  });
};

const MessageBubble = ({ m, isMe, formatTime, onContextMenu, searchQuery }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const customFont =
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif";

  // Image preview modal state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // PDF preview modal state
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Open PDF viewer by fetching as blob for native browser rendering
  const handleViewPdf = async () => {
    if (pdfBlobUrl) {
      setPdfPreviewOpen(true);
      return;
    }
    setPdfLoading(true);
    setPdfPreviewOpen(true);
    try {
      const res = await fetch(m.media.url);
      const blob = await res.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      setPdfBlobUrl(url);
    } catch (err) {
      console.error("Failed to load PDF:", err);
    } finally {
      setPdfLoading(false);
    }
  };

  // Cleanup blob URL on unmount
  React.useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // Track previous status for animation
  const [prevStatus, setPrevStatus] = React.useState(m.whatsapp?.status);
  const [animate, setAnimate] = React.useState(false);

  // Handle image click for preview
  const handleImageClick = () => {
    if (m.message_type === "IMAGE" && m.media?.url) {
      setPreviewImage(m.media.url);
      setImagePreviewOpen(true);
    }
  };

  // Check if message has media
  const hasMedia = m.media?.url && (m.message_type === "IMAGE" || m.message_type === "VIDEO" || m.message_type === "AUDIO" || m.message_type === "DOCUMENT");
  const isImageMessage = m.message_type === "IMAGE";
  const isVideoMessage = m.message_type === "VIDEO";
  const isAudioMessage = m.message_type === "AUDIO";
  const isDocumentMessage = m.message_type === "DOCUMENT";

  React.useEffect(() => {
    if (prevStatus !== m.whatsapp?.status) {
      setAnimate(true);
      setPrevStatus(m.whatsapp?.status);
      setTimeout(() => setAnimate(false), 1000);
    }
  }, [m.whatsapp?.status, prevStatus]);

  // Check if message is a template message
  const isTemplate = m.template_name || m.whatsapp?.template_name || m.type === "template";
  const templateName = m.template_name || m.whatsapp?.template_name || "";

  return (
    <Box
      id={`msg-${m._id}`}
      onContextMenu={(e) => onContextMenu(e, m)}
      sx={{
        display: "flex",
        justifyContent: isMe ? "flex-end" : "flex-start",
        mb: 1.5, // margin-bottom: 12px
        px: 2, // padding-x: 16px
        transition: "background 0.5s ease",
        borderRadius: "8px",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        animation: "fadeIn 0.3s ease-out",
      }}
    >
      <Box
        sx={{
          maxWidth: "75%",
          display: "flex",
          flexDirection: "column",
          alignItems: isMe ? "flex-end" : "flex-start",
        }}
      >
        {/* Reply Preview */}
        {m.replyTo && (
          <Box
            sx={{
              bgcolor: isMe ? alpha(primaryColor, 0.1) : "rgba(0, 0, 0, 0.05)",
              p: 1, // padding: 8px
              px: 1.5, // padding-x: 12px
              mb: -0.5, // margin-bottom: -4px
              borderRadius: "12px 12px 0 0",
              borderLeft: `4px solid ${isMe ? primaryColor : "#10b981"}`,
              display: "flex",
              alignItems: "center",
              gap: 1, // gap: 8px
              width: "fit-content",
              opacity: 0.9,
            }}
          >
            <ReplyIcon sx={{ fontSize: 16, color: isMe ? primaryColor : "#10b981" }} />
            <Typography
              noWrap
              sx={{
                fontSize: "0.8125rem", // 13px
                color: "#64748b",
                fontFamily: customFont,
                fontWeight: 500,
              }}
            >
              {parseMessageText(m.replyTo)}
            </Typography>
          </Box>
        )}

        {/* Template Badge - Show above message for outgoing */}
        {isTemplate && isMe && (
          <Chip
            label={`Template: ${templateName}`}
            size="small"
            sx={{
              height: 22, // 22px
              mb: 0.5, // margin-bottom: 4px
              bgcolor: alpha(primaryColor, 0.08),
              color: primaryColor,
              fontSize: "0.6875rem", // 11px
              fontWeight: 600,
              borderRadius: "6px",
              border: `1px solid ${alpha(primaryColor, 0.2)}`,
              "& .MuiChip-label": {
                px: 1, // padding-x: 8px
              },
            }}
          />
        )}

        {/* Main Message Bubble */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            px: isImageMessage ? 0.5 : 2, // padding-x: 4px for images
            pt: isImageMessage ? 0.5 : 1.25, // padding-top: 4px for images
            pb: 1, // padding-bottom: 8px
            bgcolor: isMe ? alpha(primaryColor, 0.08) : "#ffffff",
            borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
            border: isMe ? `1px solid ${alpha(primaryColor, 0.15)}` : "1px solid rgba(0, 0, 0, 0.06)",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            },
            overflow: "hidden",
          }}
        >
          {/* Image Display */}
          {isImageMessage && m.media?.url && (
            <Box
              sx={{
                position: "relative",
                mb: m.text ? 0.5 : 0,
              }}
            >
              <Box
                component="img"
                src={m.media.url}
                alt={m.text || "Image"}
                onClick={handleImageClick}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "block",
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              >
                <ZoomIn sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
            </Box>
          )}

          {/* Video Display */}
          {isVideoMessage && m.media?.url && (
            <Box
              sx={{
                mb: m.text ? 0.5 : 0,
              }}
            >
              <Box
                component="video"
                src={m.media.url}
                controls
                sx={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: "12px",
                  display: "block",
                }}
              />
            </Box>
          )}

          {/* Audio Display */}
          {isAudioMessage && m.media?.url && (
            <Box
              sx={{
                mb: m.text ? 0.5 : 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                component="audio"
                src={m.media.url}
                controls
                sx={{
                  maxWidth: "100%",
                  height: 40,
                }}
              />
            </Box>
          )}

          {/* Document/PDF Display */}
          {isDocumentMessage && m.media?.url && (
            <Box
              sx={{
                mb: m.text ? 0.5 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  bgcolor: isMe ? alpha(primaryColor, 0.06) : "#f8fafc",
                  borderRadius: "10px",
                  border: `1px solid ${isMe ? alpha(primaryColor, 0.15) : "#e2e8f0"}`,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <InsertDriveFile sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#1e293b",
                    }}
                  >
                    {m.media?.file_name || m.media?.filename || m.media?.original_filename || "Document.pdf"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#64748b",
                    }}
                  >
                    PDF
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleViewPdf}
                  title="View PDF"
                  sx={{
                    color: primaryColor,
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: alpha(primaryColor, 0.1) },
                  }}
                >
                  <Visibility sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href={m.media.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download"
                  sx={{
                    color: "#64748b",
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: "#f1f5f9" },
                  }}
                >
                  <Download sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Message Text (hide for documents where text is just the filename) */}
          {(m.text || !hasMedia) && !(isDocumentMessage && m.media?.url) && (
            <Typography
              sx={{
                fontSize: "0.9375rem", // 15px
                color: "#1e293b",
                fontFamily: customFont,
                lineHeight: 1.5,
                letterSpacing: "-0.01em",
                wordBreak: "break-word",
                px: isImageMessage ? 1 : 0,
                pt: isImageMessage ? 0.5 : 0,
              }}
            >
              {parseMessageText(m.text || "", searchQuery)}
            </Typography>
          )}

          {/* Timestamp and Status */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.5, // gap: 4px
              mt: 0.5, // margin-top: 4px
              minWidth: "70px",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.6875rem", // 11px
                color: "#94a3b8",
                fontWeight: 500,
                fontFamily: customFont,
              }}
            >
              {formatTime(m.created_at)}
            </Typography>

            {/* Delivery Status Icon (for outgoing messages) */}
            {isMe && (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Glow effect for read status */}
                {m.whatsapp?.status === "read" && animate && (
                  <Box
                    sx={{
                      position: "absolute",
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: alpha(primaryColor, 0.3),
                      animation: "pulse-glow 0.8s ease-out forwards",
                      "@keyframes pulse-glow": {
                        "0%": {
                          transform: "scale(0.5)",
                          opacity: 0.8,
                        },
                        "100%": {
                          transform: "scale(2)",
                          opacity: 0,
                        },
                      },
                    }}
                  />
                )}

                {/* Ripple rings for status change */}
                {animate && (
                  <>
                    <Box
                      sx={{
                        position: "absolute",
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor:
                          m.whatsapp?.status === "read"
                            ? primaryColor
                            : "#94a3b8",
                        animation: "ripple-1 0.6s ease-out forwards",
                        "@keyframes ripple-1": {
                          "0%": {
                            transform: "scale(1)",
                            opacity: 0.6,
                          },
                          "100%": {
                            transform: "scale(2.5)",
                            opacity: 0,
                          },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor:
                          m.whatsapp?.status === "read"
                            ? primaryColor
                            : "#94a3b8",
                        animation: "ripple-2 0.6s ease-out 0.15s forwards",
                        "@keyframes ripple-2": {
                          "0%": {
                            transform: "scale(1)",
                            opacity: 0.5,
                          },
                          "100%": {
                            transform: "scale(2.5)",
                            opacity: 0,
                          },
                        },
                      }}
                    />
                  </>
                )}

                {/* Main icon with bounce animation */}
                <DoneAll
                  sx={{
                    fontSize: 16,
                    color:
                      m.whatsapp?.status === "read"
                        ? primaryColor
                        : "#94a3b8",
                    position: "relative",
                    zIndex: 1,
                    animation: animate
                      ? m.whatsapp?.status === "read"
                        ? "bounce-blue 0.5s ease-out"
                        : "bounce-gray 0.4s ease-out"
                      : "none",
                    "@keyframes bounce-blue": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": {
                        transform: "scale(1.3)",
                        filter: `drop-shadow(0 0 8px ${alpha(primaryColor, 0.8)})`,
                      },
                    },
                    "@keyframes bounce-gray": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.2)" },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Image Preview Modal */}
      <Modal
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { bgcolor: "rgba(0, 0, 0, 0.9)" },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{
              position: "absolute",
              top: -48,
              right: 0,
              color: "#fff",
              zIndex: 10,
            }}
          >
            <Close sx={{ fontSize: 32 }} />
          </IconButton>
          <Box
            component="img"
            src={previewImage}
            alt="Preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "85vh",
              borderRadius: "8px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
            }}
          />
        </Box>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { bgcolor: "rgba(0, 0, 0, 0.85)" },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            maxWidth: 900,
            bgcolor: "#fff",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          }}
        >
          {/* PDF Modal Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "6px",
                  bgcolor: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InsertDriveFile sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1e293b" }}>
                {m.media?.file_name || m.media?.filename || "Document.pdf"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                component="a"
                href={m.media?.url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                title="Download"
                sx={{ color: "#64748b", "&:hover": { bgcolor: "#e2e8f0" } }}
              >
                <Download sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                onClick={() => setPdfPreviewOpen(false)}
                size="small"
                sx={{ color: "#64748b", "&:hover": { bgcolor: "#e2e8f0" } }}
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
          {/* PDF content */}
          {pdfLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "0.9375rem",
              }}
            >
              Loading PDF...
            </Box>
          ) : pdfBlobUrl ? (
            <Box
              component="iframe"
              src={pdfBlobUrl}
              title="PDF Preview"
              sx={{
                width: "100%",
                height: "calc(100% - 56px)",
                border: "none",
              }}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                fontSize: "0.875rem",
              }}
            >
              Failed to load PDF
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default MessageBubble;
